import {match} from 'ts-pattern';

import {
  CompositionTextLayer,
  FinancialProductType,
  FinancingCompositionLayer,
  FinancingCompositionScene,
} from '@dms/api/shared';

import {randomLowerCaseUUID} from 'shared/web';

import {compositionEditorLimits} from '../constants/compositionEditorLimits';
import {FinancingTemplateContent, getFinancingTemplateContent} from './getFinancingTemplateContent';

// Templates are an editable starting point, not a list of allowed positions: the inserted scene is
// an ordinary scene the user can change in any way.

export type FinancingTemplateLook = 'infoPanel' | 'overlayPanel';

type TextBox = Pick<CompositionTextLayer, 'x' | 'y' | 'width' | 'height'>;
type TextStyle = Pick<CompositionTextLayer, 'fontSize' | 'fontWeight' | 'color' | 'align'>;

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 800;
const TEXT_COLOR = '#FFFFFF';
const LABEL_COLOR = '#D6E2F5';

const INFO_PANEL = {x: 720, fill: '#0A3D8F', padding: 40, detailStartY: 280, detailHeight: 80};
const OVERLAY_PANEL = {y: 520, fill: '#0B1B33', opacity: 0.75, padding: 48};
const OVERLAY_DETAILS = {x: 540, labelWidth: 340, valueWidth: 272, startY: 552, rowHeight: 48};

const TITLE_STYLE: TextStyle = {fontSize: 28, fontWeight: 700, color: TEXT_COLOR, align: 'left'};
const LABEL_STYLE: TextStyle = {fontSize: 20, fontWeight: 400, color: LABEL_COLOR, align: 'left'};
const VALUE_STYLE: TextStyle = {fontSize: 28, fontWeight: 700, color: TEXT_COLOR, align: 'left'};

const createText = (text: string, box: TextBox, style: TextStyle): CompositionTextLayer => ({
  id: randomLowerCaseUUID(),
  type: 'text',
  ...box,
  ...style,
  opacity: 1,
  text,
  fontId: null,
  verticalAlign: 'middle',
  lineHeight: compositionEditorLimits.defaultLineHeight,
});

const createScene = (layers: FinancingCompositionLayer[]): FinancingCompositionScene => ({
  version: 1,
  canvas: {
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
    background: {color: '#FFFFFF', opacity: 1},
  },
  layers,
});

// The photo slot fills the left 60 %, an opaque panel on the right lists the offer.
const createInfoPanelScene = (content: FinancingTemplateContent) => {
  const x = INFO_PANEL.x + INFO_PANEL.padding;
  const width = CANVAS_WIDTH - INFO_PANEL.x - 2 * INFO_PANEL.padding;
  return createScene([
    {
      id: randomLowerCaseUUID(),
      type: 'photo',
      x: 0,
      y: 0,
      width: INFO_PANEL.x,
      height: CANVAS_HEIGHT,
      opacity: 1,
      fit: 'cover',
    },
    {
      id: randomLowerCaseUUID(),
      type: 'rect',
      x: INFO_PANEL.x,
      y: 0,
      width: CANVAS_WIDTH - INFO_PANEL.x,
      height: CANVAS_HEIGHT,
      opacity: 1,
      fill: INFO_PANEL.fill,
      radius: 0,
      stroke: null,
    },
    // Two lines high: a long product name wraps.
    createText(content.title, {x, y: 48, width, height: 72}, TITLE_STYLE),
    createText(content.monthlyPaymentLabel, {x, y: 136, width, height: 32}, LABEL_STYLE),
    createText(
      content.monthlyPayment,
      {x, y: 168, width, height: 80},
      {...VALUE_STYLE, fontSize: 56}
    ),
    // Each detail is a label above its value, so a long label never squeezes the value.
    ...content.details.flatMap((detail, index) => {
      const y = INFO_PANEL.detailStartY + index * INFO_PANEL.detailHeight;
      return [
        createText(detail.label, {x, y, width, height: 28}, LABEL_STYLE),
        createText(detail.value, {x, y: y + 28, width, height: 40}, VALUE_STYLE),
      ];
    }),
  ]);
};

// A full-bleed photo slot with a semi-transparent panel along the bottom edge.
const createOverlayPanelScene = (content: FinancingTemplateContent) => {
  const x = OVERLAY_PANEL.padding;
  const width = OVERLAY_DETAILS.x - 2 * OVERLAY_PANEL.padding;
  const valueX = OVERLAY_DETAILS.x + OVERLAY_DETAILS.labelWidth;
  return createScene([
    {
      id: randomLowerCaseUUID(),
      type: 'photo',
      x: 0,
      y: 0,
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      opacity: 1,
      fit: 'cover',
    },
    {
      id: randomLowerCaseUUID(),
      type: 'rect',
      x: 0,
      y: OVERLAY_PANEL.y,
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT - OVERLAY_PANEL.y,
      opacity: OVERLAY_PANEL.opacity,
      fill: OVERLAY_PANEL.fill,
      radius: 0,
      stroke: null,
    },
    createText(content.title, {x, y: 544, width, height: 72}, TITLE_STYLE),
    createText(content.monthlyPaymentLabel, {x, y: 620, width, height: 32}, LABEL_STYLE),
    createText(
      content.monthlyPayment,
      {x, y: 652, width, height: 80},
      {...VALUE_STYLE, fontSize: 60}
    ),
    // One row per detail: the label on the left, the value aligned right.
    ...content.details.flatMap((detail, index) => {
      const y = OVERLAY_DETAILS.startY + index * OVERLAY_DETAILS.rowHeight;
      const height = OVERLAY_DETAILS.rowHeight - 4;
      return [
        createText(
          detail.label,
          {x: OVERLAY_DETAILS.x, y, width: OVERLAY_DETAILS.labelWidth, height},
          LABEL_STYLE
        ),
        createText(
          detail.value,
          {x: valueX, y, width: OVERLAY_DETAILS.valueWidth, height},
          {...VALUE_STYLE, fontSize: 24, align: 'right'}
        ),
      ];
    }),
  ]);
};

export const createFinancingTemplateScene = (
  look: FinancingTemplateLook,
  financialProductType: FinancialProductType
): FinancingCompositionScene => {
  const content = getFinancingTemplateContent(financialProductType);
  return match(look)
    .with('infoPanel', () => createInfoPanelScene(content))
    .with('overlayPanel', () => createOverlayPanelScene(content))
    .exhaustive();
};
